/**
 * S09 — Deployment Workflow.
 *
 * Lists every deployment request in flight with its current stage, owner,
 * risk tier, SLA, and a quick-pick action to open the detail drawer
 * (S10 + S11). Demo Mode shows the seeded Acme requests + any session
 * submissions; Customer Mode shows the guided empty state.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Plus,
  Compass,
  ClipboardCheck,
  Clock,
  Layers,
  ArrowUpRight,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantData } from "@/contexts/TenantModeContext";
import { useEntityList } from "@/lib/useEntity";
import { SyncedIndicator } from "@/components/data/SyncedIndicator";
import { Flywheel } from "@/components/brand/Flywheel";
import { KpiTile } from "@/pages/dashboard/components/DashboardPrimitives";
import { isInFlight, isTerminal, STAGES } from "@/lib/deploymentWorkflow";
import { cn } from "@/lib/utils";
import { StageBadge, RiskPill } from "./components/DeploymentPrimitives";
import DeploymentRequestDetail from "./DeploymentRequestDetail";

const STATUS_FILTERS = [
  { id: "all", label: "All" },
  { id: "in-flight", label: "In flight" },
  { id: "stage-4", label: "Approval Required" },
  { id: "stage-6", label: "Deployment Ready" },
  { id: "terminal", label: "Closed" },
];

export default function DeploymentWorkflow() {
  const { user } = useAuth();
  // Phase 3b — pull canonical list from REST, then layer the in-session
  // overlay (newly-created requests + decision overrides) on top so the
  // drawer reflects any approvals/rejections taken during this session.
  const { items: serverRequests, loading, error, lastSyncedAt, refetch } = useEntityList("/api/deployment-requests");
  const { isCustomer, pendingEntities } = useTenantData();
  const requests = useMemo(() => {
    const overrides = pendingEntities?.requestOverrides || {};
    const pending = pendingEntities?.requests || [];
    const pendingIds = new Set(pending.map((r) => r.id));
    const withOverrides = serverRequests
      .filter((r) => !pendingIds.has(r.id))
      .map((r) => (overrides[r.id] ? { ...r, ...overrides[r.id] } : r));
    return [...pending, ...withOverrides];
  }, [serverRequests, pendingEntities]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const focusId = searchParams.get("focus");
  const [filterId, setFilterId] = useState("in-flight");
  const [selected, setSelected] = useState(null);

  // Honour ?focus=DR-... by auto-opening the detail drawer (used by the
  // Opportunity → Promote flow to land the user directly on the new DR).
  useEffect(() => {
    if (!focusId || requests.length === 0) return;
    const target = requests.find((r) => r.id === focusId);
    if (!target) return;
    // Defer state updates one tick to avoid set-state-in-effect lint warning
    // and let React commit the focusId render before re-rendering.
    const t = setTimeout(() => {
      setSelected(target);
      setFilterId("all");
      setSearchParams({}, { replace: true });
    }, 0);
    return () => clearTimeout(t);
  }, [focusId, requests, setSearchParams]);

  const filtered = useMemo(() => filterRequests(requests, filterId), [requests, filterId]);

  const openDetail = useCallback((req) => setSelected(req), []);
  const closeDetail = useCallback(() => {
    setSelected(null);
    // After the detail drawer closes (mutations may have happened),
    // refresh the canonical list so the table reflects the new stage.
    refetch();
  }, [refetch]);

  const liveSelected = selected ? requests.find((r) => r.id === selected.id) || selected : null;

  if (loading && requests.length === 0) return <DWFSkeleton />;
  if (error) return <DWFError message={error.message} />;
  if (isCustomer && requests.length === 0) {
    return <CustomerEmpty userName={user?.name?.split(" ")[0]} onSubmit={() => navigate("/identify/opportunities")} />;
  }

  return (
    <div className="relative px-6 lg:px-8 py-6 anim-fade-up" data-testid="deployment-workflow">
      <div className="absolute -right-24 -top-12 pointer-events-none">
        <Flywheel size={300} opacity={0.05} />
      </div>

      <Header lastSyncedAt={lastSyncedAt} syncing={loading} onRefetch={refetch} />
      <KpiStrip requests={requests} />

      <div className="mt-6 surface-card shadow-e1 overflow-hidden" data-testid="deploy-list">
        <FilterTabs current={filterId} setCurrent={setFilterId} requests={requests} />
        <DeploymentTable items={filtered} onSelect={openDetail} />
      </div>

      <DeploymentRequestDetail request={liveSelected} onClose={closeDetail} />
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
function filterRequests(requests, filterId) {
  switch (filterId) {
    case "in-flight":
      return requests.filter(isInFlight);
    case "stage-4":
      return requests.filter((r) => r.stage === 4 && !isTerminal(r));
    case "stage-6":
      return requests.filter((r) => r.stage === 6 && !isTerminal(r));
    case "terminal":
      return requests.filter(isTerminal);
    default:
      return requests;
  }
}

function Header({ lastSyncedAt, syncing, onRefetch }) {
  return (
    <div className="relative flex items-start justify-between gap-4 flex-wrap">
      <div>
        <p className="eyebrow">Deploy · Deployment Workflow</p>
        <h1 className="text-display-md text-ink-primary mt-1">Deployment requests</h1>
        <p className="text-ink-secondary mt-1 max-w-2xl">
          Every AI agent reaches production through the same governed pipeline. Track
          each request across the 7 checkpoints, approve · request changes · reject,
          and ship when ready.
        </p>
      </div>
      <SyncedIndicator
        lastSyncedAt={lastSyncedAt}
        loading={syncing}
        onRefetch={onRefetch}
        testId="deployment-workflow-synced"
      />
    </div>
  );
}

function DWFSkeleton() {
  return (
    <div className="px-6 lg:px-8 py-6 anim-fade-up" data-testid="deployment-workflow-loading">
      <div className="h-8 w-72 rounded-md bg-inset/60 animate-pulse" />
      <div className="mt-2 h-4 w-96 rounded-md bg-inset/40 animate-pulse" />
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-24 rounded-md surface-card animate-pulse bg-inset/30" />
        ))}
      </div>
      <div className="mt-6 h-[420px] rounded-md surface-card animate-pulse bg-inset/20" />
    </div>
  );
}

function DWFError({ message }) {
  return (
    <div className="px-6 lg:px-8 py-6" data-testid="deployment-workflow-error">
      <div className="surface-card shadow-e1 p-5 border-l-[3px] border-l-risk-high">
        <p className="eyebrow text-risk-high">Deploy · Deployment Workflow</p>
        <h1 className="text-h2 text-ink-primary mt-1">Couldn&apos;t load deployment requests</h1>
        <p className="mt-2 text-meta text-ink-secondary">{message || "Network error — please retry."}</p>
      </div>
    </div>
  );
}

function KpiStrip({ requests }) {
  const inFlight = requests.filter(isInFlight).length;
  const awaitingApproval = requests.filter((r) => r.stage === 4 && !isTerminal(r)).length;
  const ready = requests.filter((r) => r.stage === 6 && !isTerminal(r)).length;
  const deployed = requests.filter((r) => r.terminal && r.status === "deployed").length;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
      <KpiTile
        label="In-flight requests"
        value={inFlight.toString()}
        delta={`${awaitingApproval} need approval`}
        deltaTone={awaitingApproval === 0 ? "up" : "neutral"}
        hint="across all stages"
        icon={Compass}
        tone="tralenex"
        testId="deploy-kpi-inflight"
      />
      <KpiTile
        label="Awaiting approval"
        value={awaitingApproval.toString()}
        delta="SLA timers active"
        deltaTone={awaitingApproval > 0 ? "down" : "up"}
        hint="stage 4 — CAIO + Board (high risk)"
        icon={ClipboardCheck}
        tone="gold"
        testId="deploy-kpi-approval"
      />
      <KpiTile
        label="Deployment ready"
        value={ready.toString()}
        delta="Engineering slot"
        deltaTone="neutral"
        hint="stage 6"
        icon={Layers}
        tone="opp"
        testId="deploy-kpi-ready"
      />
      <KpiTile
        label="Deployed this period"
        value={deployed.toString()}
        delta="Live + governed"
        deltaTone="up"
        hint="stage 7 · terminal"
        icon={Clock}
        tone="low"
        testId="deploy-kpi-deployed"
      />
    </div>
  );
}

function FilterTabs({ current, setCurrent, requests }) {
  return (
    <div className="px-3 pt-3 pb-2 border-b border-border-subtle flex items-center gap-1.5 flex-wrap" data-testid="deploy-filters">
      {STATUS_FILTERS.map((f) => {
        const active = current === f.id;
        const count = countForFilter(requests, f.id);
        return (
          <button
            key={f.id}
            type="button"
            onClick={() => setCurrent(f.id)}
            className={cn(
              "inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-meta font-semibold transition-colors",
              active
                ? "bg-tralenex-600 text-white"
                : "text-ink-secondary hover:bg-inset",
            )}
            data-testid={`deploy-filter-${f.id}`}
            aria-pressed={active}
          >
            {f.label}
            <span
              className={cn(
                "inline-flex items-center justify-center min-w-[18px] h-[18px] rounded px-1 text-[10px] font-bold tabular-nums",
                active ? "bg-white/15 text-white" : "bg-inset text-ink-secondary",
              )}
            >
              {count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function countForFilter(requests, id) {
  return filterRequests(requests, id).length;
}

function DeploymentTable({ items, onSelect }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-inset/40 border-b border-border-subtle">
          <tr>
            {[
              "Request",
              "Stage",
              "Risk",
              "Owner",
              "Department",
              "Opportunity",
              "SLA",
              "Updated",
              "",
            ].map((h) => (
              <th key={h} className="px-3 py-2 text-left text-meta font-semibold tracking-[0.04em] uppercase text-ink-secondary">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border-subtle">
          {items.map((r) => (
            <tr
              key={r.id}
              onClick={() => onSelect(r)}
              className="hover:bg-inset/40 cursor-pointer transition-colors"
              data-testid={`deploy-row-${r.id}`}
            >
              <td className="px-3 py-2.5">
                <p className="font-medium text-ink-primary">{r.agentName}</p>
                <p className="text-meta text-ink-secondary font-mono tabular-nums">{r.id}</p>
              </td>
              <td className="px-3 py-2.5">
                <StageBadge stageId={r.stage} terminal={r.terminal} status={r.status} />
              </td>
              <td className="px-3 py-2.5">
                <RiskPill risk={r.risk} />
              </td>
              <td className="px-3 py-2.5 text-ink-primary">
                {STAGES.find((s) => s.id === r.stage)?.owner || "—"}
              </td>
              <td className="px-3 py-2.5 text-ink-secondary">{r.department}</td>
              <td className="px-3 py-2.5 font-mono text-meta text-ink-secondary tabular-nums">
                {r.opportunityId || "—"}
              </td>
              <td className="px-3 py-2.5 text-meta text-ink-secondary tabular-nums">
                {r.slaDue || "—"}
              </td>
              <td className="px-3 py-2.5 text-meta text-ink-disabled tabular-nums">
                {r.updatedAt || r.submittedAt || "—"}
              </td>
              <td className="px-3 py-2.5 text-ink-disabled">
                <ArrowUpRight size={14} />
              </td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr>
              <td colSpan={9} className="px-6 py-10 text-center text-ink-secondary">
                No deployment requests match this filter.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
function CustomerEmpty({ userName, onSubmit }) {
  return (
    <div
      className="relative min-h-[calc(100vh-112px)] px-6 lg:px-10 py-10 overflow-hidden anim-fade-up"
      data-testid="deploy-customer-empty"
    >
      <div className="absolute -right-40 -bottom-40 pointer-events-none">
        <Flywheel size={620} opacity={0.04} />
      </div>
      <div className="relative max-w-3xl">
        <p className="eyebrow">Deploy · Deployment Workflow</p>
        <h1 className="text-display-lg text-ink-primary mt-1">
          {userName ? `Welcome, ${userName}.` : "Welcome."}
        </h1>
        <p className="text-ink-secondary mt-3 max-w-xl text-[15px]">
          No deployment requests yet. Submit your first AI opportunity and ALGOS
          will route it through the 7-stage governance pipeline — from triage to
          live, with every checkpoint signed and auditable.
        </p>
        <button
          type="button"
          onClick={onSubmit}
          className="mt-6 inline-flex items-center gap-2 h-10 px-4 rounded-md bg-tralenex-600 text-white text-sm font-semibold hover:bg-tralenex-700"
          data-testid="deploy-empty-submit"
        >
          <Plus size={14} /> Submit opportunity
        </button>
      </div>
    </div>
  );
}
